import { useMemo, useState } from 'react';
import { Card, CardHolderForm, Select } from '@/components/generic';
import { temporaryNameCards } from '@/constants';
import { SelectOption } from '@/types/select';
import { useGetCardHolderDataWithId } from '@package/smart-wallet';
import {
  useGetCardHolderData,
  useUpdateUserCardHolder,
} from '@package/smart-wallet/hooks';
import { useTranslation } from 'react-i18next';

const ProfileInformation = () => {
  const { t } = useTranslation();
  const [holderId, setHolderId] = useState<number | undefined>(undefined);

  /* Queries */
  const { cardHolderInfo: cardHolderDataInfo, refetch: refetchHolders } =
    useGetCardHolderData();
  const { cardHolderInfo } = useGetCardHolderDataWithId({ holderId });

  /* Mutations */
  const {
    updateCardHolderData,
    error: updateError,
    isLoading: isUpdating,
  } = useUpdateUserCardHolder({
    onSuccess: () => {
      refetchHolders();
      // once updated the holder leaves the 'reject' status; clear selection
      setHolderId(undefined);
    },
  });

  /* Memos */
  const holderIdOptions = useMemo(
    (): SelectOption[] =>
      (cardHolderDataInfo?.records ?? [])
        .filter((record) => record.status === 'reject')
        .map((record, index) => ({
          id: index,
          name:
            temporaryNameCards[
              record.cardTypeId as keyof typeof temporaryNameCards
            ]?.name || `Holder ${record.holderId}`,
          value: record.holderId.toString(),
        })),
    [cardHolderDataInfo?.records],
  );

  return (
    <Card title={t('profile.card_holder.title')}>
      <div className='flex flex-col gap-4 p-5 pt-0 max-lg:px-3'>
        <Select
          label={t('profile.card_holder.select_rejected')}
          value={
            holderIdOptions.find(
              (option) => option.value === holderId?.toString(),
            ) || null
          }
          onChange={(selectedOption) =>
            setHolderId(
              selectedOption ? Number(selectedOption.value) : undefined,
            )
          }
          options={holderIdOptions}
          placeholder={t('profile.card_holder.select_id')}
        />

        {!holderIdOptions.length && (
          <p className='text-sm text-gray-500'>
            {t('profile.card_holder.no_options')}
          </p>
        )}
      </div>

      {!!holderId && cardHolderInfo && (
        <div className='p-5 pt-0 max-lg:px-3'>
          <CardHolderForm
            mode='edit'
            cardTypeId={cardHolderInfo.cardTypeId.toString()}
            initialValues={{
              firstName: cardHolderInfo.firstName,
              lastName: cardHolderInfo.lastName,
              areaCode: cardHolderInfo.areaCode,
              mobile: cardHolderInfo.mobile,
              birthday: cardHolderInfo.birthday,
              country: cardHolderInfo.country,
              town: cardHolderInfo.town,
              address: cardHolderInfo.address,
              postCode: cardHolderInfo.postCode,
            }}
            onSubmit={(data) =>
              updateCardHolderData({
                ...data,
                holderId,
                cardTypeId: cardHolderInfo.cardTypeId.toString(),
              })
            }
            submitLabel='Update'
            isSubmitting={isUpdating}
            error={updateError}
          />
        </div>
      )}
    </Card>
  );
};

export default ProfileInformation;
